import dayjs from "utils/dayjs";
import { FindingsDto } from "api/entities/report-data";
import type { AppointmentTypeCode } from "@/api/entities/encounters";

/**
 * Finds a package by its appointment type. Generic over any type carrying a
 * defaultAppointmentType so it works for LUNG, CAC, or any future gated type
 * without new per-type lookup code.
 */
export function findPackageByAppointmentType<
  T extends { defaultAppointmentType: AppointmentTypeCode }
>(
  packages: T[] | undefined,
  appointmentType: AppointmentTypeCode
): T | undefined {
  return packages?.find(pkg => pkg.defaultAppointmentType === appointmentType);
}

export type AgeEligibility = "eligible" | "tooYoung" | "tooOld" | "unknownAge";

interface AgeRules {
  minAge: number | null;
  maxAge: number | null;
}

/**
 * Checks age against a package's rules. Bounds are inclusive; a missing
 * bound is unbounded on that side. Returns a tri-state result (rather than a
 * boolean) so callers can pick "too young" vs. "too old" messaging without
 * re-deriving the comparison themselves.
 */
export function checkAgeEligibility(
  rules: AgeRules | null | undefined,
  age: number | null
): AgeEligibility {
  if (age === null) return "unknownAge";
  if (rules?.minAge != null && age < rules.minAge) return "tooYoung";
  if (rules?.maxAge != null && age > rules.maxAge) return "tooOld";
  return "eligible";
}

/**
 * Renders a package's age rules as prose (e.g. "aged 35-85") for use in
 * disabled/ineligibility messaging, instead of hardcoding the bounds.
 */
export function ageRangeLabel(rules: AgeRules | null | undefined): string {
  if (!rules || (rules.minAge == null && rules.maxAge == null)) {
    return "within the eligible age range";
  }
  if (rules.minAge != null && rules.maxAge != null) {
    return `aged ${rules.minAge}-${rules.maxAge}`;
  }
  return rules.minAge != null
    ? `aged ${rules.minAge} or older`
    : `aged ${rules.maxAge} or younger`;
}

const Environments = {
  Production: "production",
  UAT: "uat",
  Dev: "development",
  ETL: "etl",
  FDA: "fda",
  Staging: "staging",
  StagingUK: "staging-uk",
  ProductionUK: "production-uk"
};

/**
 * Checks if this is a production environment, such as prod or FDA
 */
export function isProduction(): boolean {
  const startEzraTracking = sessionStorage?.getItem("startEzraTracking");
  if (startEzraTracking && startEzraTracking === "true") {
    return true;
  }

  return (
    import.meta.env.MODE === Environments.Production ||
    import.meta.env.MODE === Environments.FDA ||
    import.meta.env.MODE === Environments.Staging ||
    import.meta.env.MODE === Environments.StagingUK ||
    import.meta.env.MODE === Environments.ProductionUK
  );
}

/**
 * Checks if this is a testing environment, i.e. not production
 */
export function isTestingEnv(): boolean {
  return !isProduction();
}

/**
 * Use to tell if this is dev, (or local), useful to things which are dev utils
 * but shouldn't be available for formal testing, eg. custom logout timeouts
 */
export function isDevelopmentEnv(): boolean {
  return import.meta.env.MODE === Environments.Dev;
}

/**
 * Creates a URL safe query string. Returns "" if no values are passed.
 * @param queries An object of key value pairs used to build a query string
 */
export function buildQueryString(queries: any): string {
  // Remove values which are null, undefined, or empty strings
  const filteredKeyValues = Object.entries(queries).filter(
    // Note since we don't care about key we are discarding it
    ([, value]) => value !== null && value !== undefined && value !== ""
  );
  if (filteredKeyValues.length === 0) {
    console.warn(
      `[buildQueryString] called with no valid values. ${JSON.stringify(
        queries
      )}`
    );
    return "";
  }

  let queryParams: Array<[string, string]> = [];

  // Expand arrays and check types
  for (const [key, value] of filteredKeyValues) {
    // Handle arrays
    if (Array.isArray(value)) {
      // Skip empty arrays
      if (value.length === 0) continue;

      // Validate array item types
      const invalidTypes = value
        .map(item => typeof item)
        .filter(type => !["string", "number", "boolean"].includes(type));

      if (invalidTypes.length > 0) {
        console.warn(
          `[buildQueryString] array contains non-primitive values for key "${key}". ${JSON.stringify(value)}`
        );
        continue;
      }

      // Add each array item as a separate param with the same key
      for (const item of value) {
        queryParams.push([key, `${item}`]);
      }
    } else {
      // Handle non-array values (original behavior)
      const valueType = typeof value;
      if (!["string", "number", "boolean"].includes(valueType)) {
        console.warn(
          `[buildQueryString] called with bad type for key "${key}": ${valueType}`
        );
        continue;
      }
      queryParams.push([key, `${value}`]);
    }
  }

  if (queryParams.length === 0) {
    return "";
  }

  // URL encode and join the params
  const queryString = queryParams
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join("&");
  return `?${queryString}`;
}

/**
 * Formats a phone number of the form +\d{12} in a nice way
 *
 * @param phoneNumber The phoneNumber to format nicely
 *
 * @returns The formatted phone number
 */
export function formatPhoneNumber(phoneNumber: string): string {
  if (phoneNumber.length < 9) {
    return phoneNumber;
  }

  const countryCode = phoneNumber.substr(0, 2);
  const areaCode = phoneNumber.substr(2, 3);
  const first3 = phoneNumber.substr(5, 3);
  const lastDigits = phoneNumber.substr(8);

  return `${countryCode} (${areaCode}) ${first3}-${lastDigits}`;
}

export function stringNotNullWithDelimiter(
  str: string,
  delimiter: string
): string {
  return str ? str + delimiter + " " : "";
}

type ccObject = { [key: string]: any };
export function validateCcName(v: ccObject) {
  const ret: any = {};

  // validates there is a name
  if (!v.name || "") {
    ret.name = "Please enter your name";
  }

  return ret;
}

export function getCalendarAppointmentUrl(
  startTime: string | Date,
  endTime: string | Date,
  location: string,
  textTitle: string | undefined = undefined,
  details: string | undefined = undefined
) {
  const format = "YYYYMMDDTHHmmssSSS";
  const formattedStartTime = dayjs.utc(startTime).format(format) + "Z";
  const formattedEndTime = endTime && dayjs.utc(endTime).format(format) + "Z";

  if (!textTitle) {
    textTitle = "Appointment";
  }

  if (!location) {
    location = "";
  }

  return `https://www.google.com/calendar/render?action=TEMPLATE&text=${textTitle}&location=${window.encodeURIComponent(
    location
  )}&dates=${formattedStartTime}${
    formattedEndTime ? `%2F${formattedEndTime}` : ""
  }&details=${details ? window.encodeURIComponent(details) : ""}`;
}

export function getCookie(name: string) {
  return document.cookie
    ?.split("; ")
    ?.find(row => row.startsWith(`${name}=`))
    ?.split("=")[1];
}

export function deleteCookie(
  name: string,
  path: string,
  domain: string | undefined
) {
  if (getCookie(name)) {
    document.cookie =
      name +
      "=" +
      (path ? ";path=" + path : "") +
      (domain ? ";domain=" + domain : "") +
      ";expires=Thu, 01 Jan 1970 00:00:01 GMT";
  }
}

export function getBrowserTimeZone(
  nameType: "long" | "short" | undefined = "long"
) {
  const timeZoneName = Intl.DateTimeFormat(undefined, {
    timeZoneName: nameType
  })
    .formatToParts(new Date())
    .find(part => part.type == "timeZoneName")?.value;

  return timeZoneName;
}

export function downloadHelper(
  fileTempUrl: string,
  fileName: string,
  newPage: boolean
) {
  const anchor = document.createElement("a");
  document.body.appendChild(anchor);

  anchor.href = fileTempUrl;
  anchor.download = fileName;
  if (newPage) {
    anchor.target = "_blank";
  }
  anchor.click();

  window.URL.revokeObjectURL(fileTempUrl);
}

export function groupBy(xs: any, key: any): any {
  return xs.reduce((groupObj: any, finding: any) => {
    (groupObj[finding[key]] = groupObj[finding[key]] || []).push(finding);
    return groupObj;
  }, {});
}

export function sortEScore(
  finding: FindingsDto,
  hasSubFinding: boolean,
  eScore: number,
  hasPotentialCancer: boolean
): any {
  // we need to nab the subfinding snippets if they exist,
  // and include them
  if (hasSubFinding) {
    const includingSubLevelSnippets = [
      ...finding.snippets,
      ...finding.subFindings.flatMap(s => s.snippets)
    ];

    // now find the highest escore
    eScore =
      includingSubLevelSnippets && includingSubLevelSnippets.length > 1
        ? includingSubLevelSnippets.sort((x, y) =>
            y ? x.eScore - y.eScore : x.eScore
          )[includingSubLevelSnippets.length - 1]?.eScore
        : (includingSubLevelSnippets[0]?.eScore ?? 0);

    // find if there is any that has a potential cancer
    hasPotentialCancer = includingSubLevelSnippets
      ? finding.snippets.filter(s => s.potentialCancer).length > 0
      : false;
  } else {
    eScore =
      finding.snippets && finding.snippets.length > 1
        ? finding.snippets.sort((x, y) => (y ? x.eScore - y.eScore : x.eScore))[
            finding.snippets.length - 1
          ]?.eScore
        : (finding.snippets[0]?.eScore ?? 0);

    hasPotentialCancer = finding.snippets
      ? finding.snippets.filter(s => s.potentialCancer).length > 0
      : false;
  }
  return { eScore, hasPotentialCancer };
}

function dec2hex(dec: number) {
  return dec.toString(16).padStart(2, "0");
}

export function generateId(len: number) {
  const arr = new Uint8Array((len || 40) / 2);
  window.crypto.getRandomValues(arr);
  return Array.from(arr, dec2hex).join("");
}

export function generateCodeVerifier(): string {
  const array = new Uint32Array(56 / 2);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec2hex).join("");
}

async function sha256(message: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  const data = encoder.encode(message);
  return window.crypto.subtle.digest("SHA-256", data);
}

function base64urlencode(a: ArrayBuffer) {
  let str = "";
  const bytes = new Uint8Array(a);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function generateCodeChallengeFromVerifier(v: string) {
  const hashed = await sha256(v);
  const base64encoded = base64urlencode(hashed);
  return base64encoded;
}

export async function getSha256Hash(val: string) {
  const bytes = await sha256(val);
  const bytesArray = Array.from(new Uint8Array(bytes));
  return bytesArray.map(item => item.toString(16).padStart(2, "0")).join("");
}

export async function getSha256Hashes(vals: string[]) {
  return Promise.all(vals.map(val => getSha256Hash(val)));
}
